import { useState } from 'react';
import useSWR from 'swr';
import { Users, UserPlus, UserMinus, UserCheck, FileText, ChevronRight, CreditCard, Phone, MapPin, AlertCircle, Pencil } from 'lucide-react';
import apiClient from '@/lib/api';

// ─── Tipos ────────────────────────────────────────────────────────────────────

type EmployeeEstado = 'activo' | 'baja' | 'excedencia';

interface PayrollEstimate {
  salario_base: number;
  salario_base_fmt: string;
  coste_empresa_total: number;
  coste_empresa_fmt: string;
}

interface Employee {
  id: string;
  nombre: string;
  apellidos: string;
  nif: string;
  email: string;
  telefono: string | null;
  cargo: string;
  departamento: string | null;
  fecha_contratacion: string;
  fecha_baja: string | null;
  salario_base: number;
  cuenta_bancaria: string | null;
  iban: string | null;
  ss_number: string | null;
  direccion: string | null;
  city: string | null;
  postal_code: string | null;
  province: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  birth_date: string | null;
  estado: EmployeeEstado;
  payroll: PayrollEstimate;
}

interface SalaryEntry {
  id: string;
  base_salary: number;
  effective_date: string;
  source: 'manual' | 'ocr_nomina' | 'contract';
  notes: string | null;
}

interface RecentDoc {
  id: string;
  nombre: string;
  ocr_status: string;
  validation_status: string;
  created_at: string;
}

interface EmployeesResponse {
  status: string;
  total: number;
  data: Employee[];
}

interface CreateEmployeeDto {
  // Pestaña 1 — Datos Personales
  nombre: string;
  apellidos: string;
  nif: string;
  email: string;
  telefono: string;
  birth_date: string;
  ss_number: string;
  direccion: string;
  city: string;
  postal_code: string;
  province: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  // Pestaña 2 — Datos Contractuales
  cargo: string;
  departamento: string;
  fecha_contratacion: string;
  salario_base: string;
  cuenta_bancaria: string;
  iban: string;
}

// ─── Fetcher ──────────────────────────────────────────────────────────────────

async function fetcher<T>(url: string): Promise<T> {
  const res = await apiClient.get<T>(url);
  return res.data;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const ESTADO_CFG: Record<EmployeeEstado, { label: string; color: string; bg: string }> = {
  activo:     { label: 'Activo',     color: 'var(--color-success)', bg: 'color-mix(in srgb, var(--color-success) 12%, transparent)' },
  baja:       { label: 'Baja',       color: 'var(--color-danger)',  bg: 'color-mix(in srgb, var(--color-danger) 12%, transparent)' },
  excedencia: { label: 'Excedencia', color: 'var(--color-warning)', bg: 'color-mix(in srgb, var(--color-warning) 12%, transparent)' },
};

const ESTADOS_FILTRO = [
  { value: '',           label: 'Todos los estados' },
  { value: 'activo',     label: 'Activos' },
  { value: 'baja',       label: 'Baja' },
  { value: 'excedencia', label: 'Excedencia' },
];

const EMPTY_FORM: CreateEmployeeDto = {
  nombre: '', apellidos: '', nif: '', email: '', telefono: '',
  birth_date: '', ss_number: '', direccion: '', city: '', postal_code: '',
  province: '', emergency_contact_name: '', emergency_contact_phone: '',
  cargo: '', departamento: '', fecha_contratacion: '',
  salario_base: '', cuenta_bancaria: '', iban: '',
};

// ─── Estilos compartidos ──────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '0.5rem 0.75rem',
  background: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: 6, fontSize: '0.875rem', color: 'var(--color-text)',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.75rem', fontWeight: 500,
  color: 'var(--color-muted)', marginBottom: 4,
};

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function EstadoBadge({ estado }: { estado: EmployeeEstado }) {
  const cfg = ESTADO_CFG[estado] ?? { label: estado, color: 'var(--color-muted)', bg: 'transparent' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: '0.75rem', fontWeight: 600, color: cfg.color, background: cfg.bg,
      borderRadius: 4, padding: '2px 8px', textTransform: 'capitalize',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr>
      {Array.from({ length: 7 }).map((_, i) => (
        <td key={i} style={{ padding: '0.875rem 1rem' }}>
          <div style={{ height: 14, borderRadius: 4, background: 'var(--color-border)', width: i === 0 ? '80%' : '90%' }} />
        </td>
      ))}
    </tr>
  );
}

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <label style={labelStyle}>{label}{required && <span style={{ color: 'var(--color-danger)' }}> *</span>}</label>
      {children}
    </div>
  );
}

// ─── Modal Nuevo Empleado con pestañas ────────────────────────────────────────

function NewEmployeeModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [tab, setTab] = useState<'personal' | 'contractual'>('personal');
  const [form, setForm] = useState<CreateEmployeeDto>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await apiClient.post('/rrhh/employees', { ...form, salario_base: Number(form.salario_base) });
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Error al crear el empleado.');
      setSaving(false);
    }
  }

  const tabBtn = (id: typeof tab, label: string) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      style={{
        padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', cursor: 'pointer',
        fontSize: '0.875rem', fontWeight: 500,
        background: tab === id ? 'var(--color-primary)' : 'transparent',
        color: tab === id ? '#fff' : 'var(--color-muted)',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 28, width: '100%', maxWidth: 620, maxHeight: '92vh', overflowY: 'auto', }} onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--color-text)' }}>Nuevo empleado</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-muted)', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        {/* Pestañas */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, padding: 4, background: 'var(--color-bg)', borderRadius: 8 }}>
          {tabBtn('personal', 'Datos Personales')}
          {tabBtn('contractual', 'Datos Contractuales')}
        </div>

        {error && (
          <div style={{ background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-danger) 30%, transparent)', borderRadius: 6, padding: '0.75rem', marginBottom: 16, fontSize: '0.8125rem', color: 'var(--color-danger)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {tab === 'personal' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Nombre" required><input name="nombre" value={form.nombre} onChange={handleChange} required style={inputStyle} /></Field>
              <Field label="Apellidos" required><input name="apellidos" value={form.apellidos} onChange={handleChange} required style={inputStyle} /></Field>
              <Field label="NIF / NIE" required><input name="nif" value={form.nif} onChange={handleChange} required placeholder="12345678A" style={inputStyle} /></Field>
              <Field label="Email" required><input name="email" type="email" value={form.email} onChange={handleChange} required style={inputStyle} /></Field>
              <Field label="Telefono"><input name="telefono" type="tel" value={form.telefono} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="Fecha de nacimiento"><input name="birth_date" type="date" value={form.birth_date} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="N° Seg. Social"><input name="ss_number" value={form.ss_number} onChange={handleChange} placeholder="123456789012" style={inputStyle} /></Field>
              <div />
              <Field label="Direccion" ><input name="direccion" value={form.direccion} onChange={handleChange} style={{ ...inputStyle, gridColumn: '1 / -1' }} /></Field>
              <Field label="Ciudad"><input name="city" value={form.city} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="Codigo postal"><input name="postal_code" value={form.postal_code} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="Provincia"><input name="province" value={form.province} onChange={handleChange} style={inputStyle} /></Field>
              <div />
              <Field label="Contacto emergencia — nombre"><input name="emergency_contact_name" value={form.emergency_contact_name} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="Contacto emergencia — telefono"><input name="emergency_contact_phone" type="tel" value={form.emergency_contact_phone} onChange={handleChange} style={inputStyle} /></Field>
            </div>
          )}

          {tab === 'contractual' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Cargo" required><input name="cargo" value={form.cargo} onChange={handleChange} required style={inputStyle} /></Field>
              <Field label="Departamento"><input name="departamento" value={form.departamento} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="Fecha de contratacion" required><input name="fecha_contratacion" type="date" value={form.fecha_contratacion} onChange={handleChange} required style={inputStyle} /></Field>
              <Field label="Salario base (EUR/mes)" required><input name="salario_base" type="number" value={form.salario_base} onChange={handleChange} required placeholder="1800" style={inputStyle} /></Field>
              <Field label="Cuenta bancaria"><input name="cuenta_bancaria" value={form.cuenta_bancaria} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="IBAN"><input name="iban" value={form.iban} onChange={handleChange} placeholder="ES00 0000 0000 0000 0000 0000" style={inputStyle} /></Field>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 24 }}>
            {tab === 'personal'
              ? <div />
              : <button type="button" onClick={() => setTab('personal')} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '0.875rem' }}>Anterior</button>
            }
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={onClose} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '0.875rem' }}>Cancelar</button>
              {tab === 'personal'
                ? <button type="button" onClick={() => setTab('contractual')} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}>Siguiente</button>
                : <button type="submit" disabled={saving} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontSize: '0.875rem', fontWeight: 500 }}>{saving ? 'Guardando...' : 'Crear empleado'}</button>
              }
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal Editar Empleado ────────────────────────────────────────────────────

function EditEmployeeModal({ employee, onClose, onSuccess }: { employee: Employee; onClose: () => void; onSuccess: (updated: Employee) => void }) {
  const [tab, setTab] = useState<'personal' | 'contractual'>('personal');
  const [form, setForm] = useState<CreateEmployeeDto>({
    nombre:                  employee.nombre,
    apellidos:               employee.apellidos,
    nif:                     employee.nif,
    email:                   employee.email,
    telefono:                employee.telefono ?? '',
    birth_date:              employee.birth_date?.slice(0, 10) ?? '',
    ss_number:               employee.ss_number ?? '',
    direccion:               employee.direccion ?? '',
    city:                    employee.city ?? '',
    postal_code:             employee.postal_code ?? '',
    province:                employee.province ?? '',
    emergency_contact_name:  employee.emergency_contact_name ?? '',
    emergency_contact_phone: employee.emergency_contact_phone ?? '',
    cargo:                   employee.cargo,
    departamento:            employee.departamento ?? '',
    fecha_contratacion:      employee.fecha_contratacion?.slice(0, 10) ?? '',
    salario_base:            String(employee.salario_base),
    cuenta_bancaria:         employee.cuenta_bancaria ?? '',
    iban:                    employee.iban ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await apiClient.patch<{ data: Employee }>(`/rrhh/employees/${employee.id}`, {
        ...form,
        salario_base: Number(form.salario_base),
      });
      onSuccess(res.data.data);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Error al guardar los cambios.');
      setSaving(false);
    }
  }

  const tabBtn = (id: typeof tab, label: string) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      style={{
        padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', cursor: 'pointer',
        fontSize: '0.875rem', fontWeight: 500,
        background: tab === id ? 'var(--color-primary)' : 'transparent',
        color: tab === id ? '#fff' : 'var(--color-muted)',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 110, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 28, width: '100%', maxWidth: 620, maxHeight: '92vh', overflowY: 'auto', }} onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--color-text)' }}>
            Editar — {employee.nombre} {employee.apellidos}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-muted)', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 20, padding: 4, background: 'var(--color-bg)', borderRadius: 8 }}>
          {tabBtn('personal', 'Datos Personales')}
          {tabBtn('contractual', 'Datos Contractuales')}
        </div>

        {error && (
          <div style={{ background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-danger) 30%, transparent)', borderRadius: 6, padding: '0.75rem', marginBottom: 16, fontSize: '0.8125rem', color: 'var(--color-danger)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {tab === 'personal' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Nombre" required><input name="nombre" value={form.nombre} onChange={handleChange} required style={inputStyle} /></Field>
              <Field label="Apellidos" required><input name="apellidos" value={form.apellidos} onChange={handleChange} required style={inputStyle} /></Field>
              <Field label="NIF / NIE" required><input name="nif" value={form.nif} onChange={handleChange} required placeholder="12345678A" style={inputStyle} /></Field>
              <Field label="Email" required><input name="email" type="email" value={form.email} onChange={handleChange} required style={inputStyle} /></Field>
              <Field label="Telefono"><input name="telefono" type="tel" value={form.telefono} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="Fecha de nacimiento"><input name="birth_date" type="date" value={form.birth_date} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="N° Seg. Social"><input name="ss_number" value={form.ss_number} onChange={handleChange} placeholder="123456789012" style={inputStyle} /></Field>
              <div />
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Direccion"><input name="direccion" value={form.direccion} onChange={handleChange} style={inputStyle} /></Field>
              </div>
              <Field label="Ciudad"><input name="city" value={form.city} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="Codigo postal"><input name="postal_code" value={form.postal_code} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="Provincia"><input name="province" value={form.province} onChange={handleChange} style={inputStyle} /></Field>
              <div />
              <Field label="Contacto emergencia — nombre"><input name="emergency_contact_name" value={form.emergency_contact_name} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="Contacto emergencia — telefono"><input name="emergency_contact_phone" type="tel" value={form.emergency_contact_phone} onChange={handleChange} style={inputStyle} /></Field>
            </div>
          )}

          {tab === 'contractual' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Cargo" required><input name="cargo" value={form.cargo} onChange={handleChange} required style={inputStyle} /></Field>
              <Field label="Departamento"><input name="departamento" value={form.departamento} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="Fecha de contratacion" required><input name="fecha_contratacion" type="date" value={form.fecha_contratacion} onChange={handleChange} required style={inputStyle} /></Field>
              <Field label="Salario base (EUR/mes)" required><input name="salario_base" type="number" value={form.salario_base} onChange={handleChange} required placeholder="1800" style={inputStyle} /></Field>
              <Field label="Cuenta bancaria"><input name="cuenta_bancaria" value={form.cuenta_bancaria} onChange={handleChange} style={inputStyle} /></Field>
              <Field label="IBAN"><input name="iban" value={form.iban} onChange={handleChange} placeholder="ES00 0000 0000 0000 0000 0000" style={inputStyle} /></Field>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 24 }}>
            {tab === 'personal'
              ? <div />
              : <button type="button" onClick={() => setTab('personal')} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '0.875rem' }}>Anterior</button>
            }
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={onClose} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '0.875rem' }}>Cancelar</button>
              {tab === 'personal'
                ? <button type="button" onClick={() => setTab('contractual')} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}>Siguiente</button>
                : <button type="submit" disabled={saving} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontSize: '0.875rem', fontWeight: 500 }}>{saving ? 'Guardando...' : 'Guardar cambios'}</button>
              }
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal Dar de Baja ────────────────────────────────────────────────────────

function BajaModal({ employee, onClose, onSuccess }: { employee: Employee; onClose: () => void; onSuccess: () => void }) {
  const [fechaBaja, setFechaBaja] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setSaving(true);
    setError(null);
    try {
      await apiClient.patch(`/rrhh/employees/${employee.id}/status`, { estado: 'baja', fecha_baja: fechaBaja });
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Error al dar de baja al empleado.');
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 28, width: '100%', maxWidth: 420, }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <UserMinus size={18} color="var(--color-danger)" />
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--color-text)' }}>Dar de baja</h2>
        </div>
        <p style={{ fontSize: '0.875rem', color: 'var(--color-muted)', margin: '0 0 16px' }}>
          Vas a dar de baja a <strong style={{ color: 'var(--color-text)' }}>{employee.nombre} {employee.apellidos}</strong>. Esta accion es reversible.
        </p>
        {error && <div style={{ background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-danger) 30%, transparent)', borderRadius: 6, padding: '0.625rem', marginBottom: 14, fontSize: '0.8125rem', color: 'var(--color-danger)' }}>{error}</div>}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Fecha de baja</label>
          <input type="date" value={fechaBaja} onChange={e => setFechaBaja(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '0.875rem' }}>Cancelar</button>
          <button onClick={handleConfirm} disabled={saving} style={{ padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', background: 'var(--color-danger)', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontSize: '0.875rem', fontWeight: 500 }}>{saving ? 'Procesando...' : 'Confirmar baja'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Panel lateral: Ficha del empleado ───────────────────────────────────────

function EmployeeSheet({ employee: initialEmployee, onClose, onBaja }: { employee: Employee; onClose: () => void; onBaja: () => void }) {
  const [tab,      setTab]      = useState<'info' | 'docs' | 'salary'>('info');
  const [employee, setEmployee] = useState<Employee>(initialEmployee);
  const [editing,  setEditing]  = useState(false);

  const { data: docsData } = useSWR<{ data: RecentDoc[] }>(
    `/rrhh/employees/${employee.id}/recent-documents`, fetcher
  );
  const { data: salaryData } = useSWR<{ data: SalaryEntry[] }>(
    `/rrhh/employees/${employee.id}/salary-history`, fetcher
  );

  const EUR = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });

  const sheetTab = (id: typeof tab, label: string) => (
    <button
      onClick={() => setTab(id)}
      style={{
        flex: 1, padding: '0.5rem', border: 'none', borderRadius: 6, cursor: 'pointer',
        fontSize: '0.8125rem', fontWeight: 500,
        background: tab === id ? 'var(--color-primary)' : 'transparent',
        color: tab === id ? '#fff' : 'var(--color-muted)',
      }}
    >
      {label}
    </button>
  );

  const InfoRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | null | undefined }) =>
    value ? (
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
        <span style={{ color: 'var(--color-muted)', flexShrink: 0, marginTop: 2 }}>{icon}</span>
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-muted)' }}>{label}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text)', marginTop: 1 }}>{value}</div>
        </div>
      </div>
    ) : null;

  const validationColor = (s: string) => s === 'verified' ? '#10b981' : '#f59e0b';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex' }} onClick={onClose}>
      {/* Overlay */}
      <div style={{ flex: 1, background: 'rgba(0,0,0,0.4)' }} />

      {/* Sheet */}
      <div style={{ width: 420, maxWidth: '100vw', background: 'var(--color-surface)', borderLeft: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', overflowY: 'auto', }} onClick={e => e.stopPropagation()}>

        {editing && (
          <EditEmployeeModal
            employee={employee}
            onClose={() => setEditing(false)}
            onSuccess={updated => setEmployee(updated)}
          />
        )}

        {/* Header */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--color-border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--color-text)' }}>{employee.apellidos}, {employee.nombre}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-muted)', marginTop: 2 }}>{employee.cargo}{employee.departamento ? ` · ${employee.departamento}` : ''}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button
                onClick={() => setEditing(true)}
                title="Editar datos del empleado"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.3rem 0.75rem', borderRadius: 5, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500, }}
              >
                <Pencil size={12} /> Editar
              </button>
              <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-muted)', fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
            <EstadoBadge estado={employee.estado} />
            <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>{employee.nif}</span>
          </div>
          {employee.estado === 'activo' && (
            <button onClick={onBaja} style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0.3rem 0.875rem', borderRadius: 5, border: '1px solid color-mix(in srgb, var(--color-danger) 40%, transparent)', background: 'color-mix(in srgb, var(--color-danger) 8%, transparent)', color: 'var(--color-danger)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500, }}>
              <UserMinus size={12} /> Dar de baja
            </button>
          )}
        </div>

        {/* Pestañas */}
        <div style={{ display: 'flex', gap: 4, padding: '12px 20px', background: 'var(--color-bg)' }}>
          {sheetTab('info', 'Expediente')}
          {sheetTab('docs', 'Documentos')}
          {sheetTab('salary', 'Salarios')}
        </div>

        {/* Contenido */}
        <div style={{ padding: '0 20px 20px', flex: 1 }}>

          {tab === 'info' && (
            <div style={{ paddingTop: 12 }}>
              <InfoRow icon={<Phone size={14} />} label="Telefono" value={employee.telefono} />
              <InfoRow icon={<FileText size={14} />} label="N° Seg. Social" value={employee.ss_number} />
              <InfoRow icon={<CreditCard size={14} />} label="IBAN" value={employee.iban ?? employee.cuenta_bancaria} />
              <InfoRow icon={<MapPin size={14} />} label="Direccion" value={[employee.direccion, employee.city, employee.postal_code, employee.province].filter(Boolean).join(', ')} />
              <InfoRow icon={<AlertCircle size={14} />} label="Emergencias" value={employee.emergency_contact_name ? `${employee.emergency_contact_name} — ${employee.emergency_contact_phone ?? ''}` : null} />
              <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--color-bg)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-muted)', marginBottom: 8 }}>Coste empresa estimado</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem' }}>
                  <span style={{ color: 'var(--color-muted)' }}>Salario base</span>
                  <span style={{ color: 'var(--color-text)', fontWeight: 500 }}>{employee.payroll?.salario_base_fmt ?? `${employee.salario_base} €`}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', marginTop: 4 }}>
                  <span style={{ color: 'var(--color-muted)' }}>Coste empresa (+30% SS)</span>
                  <span style={{ color: 'var(--color-text)', fontWeight: 600 }}>{employee.payroll?.coste_empresa_fmt}</span>
                </div>
              </div>
            </div>
          )}

          {tab === 'docs' && (
            <div style={{ paddingTop: 12 }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-muted)', marginBottom: 12 }}>Ultimas nominas vinculadas</div>
              {!docsData?.data?.length
                ? <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--color-muted)', fontSize: '0.875rem' }}>No hay documentos vinculados</div>
                : docsData.data.map(doc => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--color-border)' }}>
                    <FileText size={16} color="var(--color-muted)" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.875rem', color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.nombre}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: 1 }}>{new Date(doc.created_at).toLocaleDateString('es-ES')}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: validationColor(doc.validation_status), background: `color-mix(in srgb, ${validationColor(doc.validation_status)} 12%, transparent)`, borderRadius: 4, padding: '2px 7px', flexShrink: 0 }}>
                      {doc.validation_status === 'verified' ? 'Verificado' : 'IA'}
                    </span>
                    <ChevronRight size={14} color="var(--color-muted)" style={{ flexShrink: 0 }} />
                  </div>
                ))
              }
            </div>
          )}

          {tab === 'salary' && (
            <div style={{ paddingTop: 12 }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-muted)', marginBottom: 12 }}>Historial de salarios</div>
              {!salaryData?.data?.length
                ? <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--color-muted)', fontSize: '0.875rem' }}>No hay entradas en el historial</div>
                : salaryData.data.map(entry => (
                  <div key={entry.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--color-border)' }}>
                    <div>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text)' }}>{EUR.format(entry.base_salary)}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: 1 }}>
                        {new Date(entry.effective_date).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                        {entry.source === 'ocr_nomina' && <span style={{ marginLeft: 6, color: '#818cf8' }}>OCR</span>}
                      </div>
                    </div>
                    {entry.notes && <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', maxWidth: 140, textAlign: 'right' }}>{entry.notes}</div>}
                  </div>
                ))
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export function EmployeesPage() {
  const [estadoFiltro, setEstadoFiltro] = useState<string>('activo');
  const [deptFiltro,   setDeptFiltro]   = useState<string>('');
  const [showNew,      setShowNew]      = useState(false);
  const [bajaTarget,   setBajaTarget]   = useState<Employee | null>(null);
  const [selectedEmp,  setSelectedEmp]  = useState<Employee | null>(null);

  const swrKey = `/rrhh/employees${estadoFiltro ? `?estado=${estadoFiltro}` : ''}${deptFiltro ? `&cargo=${encodeURIComponent(deptFiltro)}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR<EmployeesResponse>(swrKey, fetcher);

  const employees = data?.data ?? [];
  const departamentos = Array.from(new Set(employees.map(e => e.departamento).filter(Boolean) as string[])).sort();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', }}>

      {showNew && <NewEmployeeModal onClose={() => setShowNew(false)} onSuccess={() => mutate()} />}
      {bajaTarget && <BajaModal employee={bajaTarget} onClose={() => setBajaTarget(null)} onSuccess={() => { mutate(); setSelectedEmp(null); }} />}
      {selectedEmp && <EmployeeSheet employee={selectedEmp} onClose={() => setSelectedEmp(null)} onBaja={() => { setSelectedEmp(null); setBajaTarget(selectedEmp); }} />}

      {/* Cabecera */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Empleados</h1>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: 'var(--color-muted)' }}>
            {isLoading ? 'Cargando...' : `${data?.total ?? 0} empleados encontrados`}
          </p>
        </div>
        <button onClick={() => setShowNew(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.5rem 1rem', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, flexShrink: 0 }}>
          <UserPlus size={15} /> Nuevo empleado
        </button>
      </div>

      {/* Tarjetas resumen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
        {[
          { label: 'Total',      value: data?.total ?? 0,                                       icon: <Users size={14} />,     color: 'var(--color-primary)' },
          { label: 'Activos',    value: employees.filter(e => e.estado === 'activo').length,    icon: <UserCheck size={14} />, color: 'var(--color-success)' },
          { label: 'Baja',       value: employees.filter(e => e.estado === 'baja').length,      icon: <UserMinus size={14} />, color: 'var(--color-danger)' },
          { label: 'Excedencia', value: employees.filter(e => e.estado === 'excedencia').length,icon: <UserMinus size={14} />, color: 'var(--color-warning)' },
        ].map(card => (
          <div key={card.label} style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, padding: '0.875rem 1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{card.label}</span>
              <span style={{ color: card.color }}>{card.icon}</span>
            </div>
            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: card.color }}>{isLoading ? '—' : card.value}</span>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <select value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)} style={{ padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: '0.875rem', cursor: 'pointer', }}>
          {ESTADOS_FILTRO.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {departamentos.length > 0 && (
          <select value={deptFiltro} onChange={e => setDeptFiltro(e.target.value)} style={{ padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: '0.875rem', cursor: 'pointer', }}>
            <option value="">Todos los departamentos</option>
            {departamentos.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
      </div>

      {error && (
        <div role="alert" style={{ background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-danger) 30%, transparent)', borderRadius: 8, padding: '0.875rem 1rem', fontSize: '0.8125rem', color: 'var(--color-danger)' }}>
          No se pudo cargar la lista de empleados.
        </div>
      )}

      {/* Tabla */}
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-table-header)' }}>
              {['Empleado', 'Cargo / Dpto', 'NIF', 'Contratacion', 'Salario base', 'Estado', 'Acciones'].map(h => (
                <th key={h} style={{ padding: '0.625rem 1rem', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
              : employees.length === 0
              ? (
                <tr>
                  <td colSpan={7} style={{ padding: '3rem 1rem', textAlign: 'center', color: 'var(--color-muted)', fontSize: '0.875rem' }}>
                    No hay empleados con los filtros seleccionados.
                  </td>
                </tr>
              )
              : employees.map((emp, i) => (
                <tr
                  key={emp.id}
                  style={{ borderBottom: i < employees.length - 1 ? '1px solid var(--color-border)' : 'none', cursor: 'pointer', transition: 'background 0.1s' }}
                  onClick={() => setSelectedEmp(emp)}
                  onMouseEnter={e => (e.currentTarget.style.background = 'color-mix(in srgb, var(--color-primary) 4%, transparent)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ fontWeight: 600, color: 'var(--color-text)' }}>{emp.apellidos}, {emp.nombre}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: 1 }}>{emp.email}</div>
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ color: 'var(--color-text)' }}>{emp.cargo}</div>
                    {emp.departamento && <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: 1 }}>{emp.departamento}</div>}
                  </td>
                  <td style={{ padding: '0.875rem 1rem', color: 'var(--color-muted)' }}>{emp.nif}</td>
                  <td style={{ padding: '0.875rem 1rem', color: 'var(--color-muted)', whiteSpace: 'nowrap' }}>{new Date(emp.fecha_contratacion).toLocaleDateString('es-ES')}</td>
                  <td style={{ padding: '0.875rem 1rem', color: 'var(--color-text)', whiteSpace: 'nowrap' }}>{emp.payroll?.salario_base_fmt ?? `${emp.salario_base} €`}</td>
                  <td style={{ padding: '0.875rem 1rem' }}><EstadoBadge estado={emp.estado} /></td>
                  <td style={{ padding: '0.875rem 1rem' }} onClick={e => e.stopPropagation()}>
                    {emp.estado === 'activo' && (
                      <button
                        onClick={() => setBajaTarget(emp)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.3rem 0.75rem', borderRadius: 5, border: '1px solid color-mix(in srgb, var(--color-danger) 40%, transparent)', background: 'color-mix(in srgb, var(--color-danger) 8%, transparent)', color: 'var(--color-danger)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500, }}
                      >
                        <UserMinus size={12} /> Baja
                      </button>
                    )}
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

    </div>
  );
}
